import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllCategories } from '../services/categoryService';
import { getAllServices } from '../services/serviceService';
import { motion } from 'framer-motion';
import { FaChevronRight, FaChevronDown, FaCheck, FaSearch, FaTimes, FaFilter, FaYoutube } from 'react-icons/fa';
// Import Firestore directly for fallback
import { db } from '../firebase/client';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import Service from '../models/Service';
import PageHero from '../components/PageHero';

const AllCategories = () => {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [showAllResults, setShowAllResults] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Fetch all active categories
        const categoriesData = await getAllCategories(true);
        if (!categoriesData || categoriesData.length === 0) {
          console.log('No categories found or categories data is empty');
        } else {
          console.log(`Fetched ${categoriesData.length} categories`);
        }
        setCategories(categoriesData || []);
        
        // Use a better approach for fetching services with retry mechanism
        await fetchServices();
      } catch (err) {
        console.error('Error in initial data fetch:', err);
        setError('Failed to load categories and services. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Search services when search query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      setIsSearching(true);
      
      // Convert search query to lowercase for case-insensitive comparison
      const query = searchQuery.toLowerCase();
      
      // Filter services based on search query
      let results = services.filter(service => 
        // Search in name
        service.name?.toLowerCase().includes(query) || 
        // Search in description
        service.description?.toLowerCase().includes(query) ||
        // Search in features
        (service.features && Array.isArray(service.features) && 
          service.features.some(feature => feature.toLowerCase().includes(query)))
      );
      
      // Apply category filter if not "all"
      if (selectedCategoryFilter !== 'all') {
        results = results.filter(service => service.categoryId === selectedCategoryFilter);
      }
      
      console.log(`Found ${results.length} services matching "${searchQuery}"${selectedCategoryFilter !== 'all' ? ' in selected category' : ''}`);
      setSearchResults(results);
    } else {
      setIsSearching(false);
      setSearchResults([]);
      setShowAllResults(false);
    }
  }, [searchQuery, services, selectedCategoryFilter]);

  // Dedicated function to fetch services with retry
  const fetchServices = async (retryCount = 0) => {
    try {
      console.log(`Attempting to fetch services (attempt ${retryCount + 1})`);
      
      // Try fetching all services through the service function first
      let servicesData;
      try {
        servicesData = await getAllServices(true);
      } catch (serviceError) {
        console.error('Error using service function, trying direct Firestore access:', serviceError);
        
        // If the service function fails, try direct Firestore access as fallback
        // Using a simpler query to avoid index requirements
        // First, get all services without filtering
        const servicesCollection = collection(db, 'services');
        
        // Don't use compound queries that require indexes
        const querySnapshot = await getDocs(servicesCollection);
        
        // Then filter the active ones client-side
        servicesData = querySnapshot.docs
          .map(doc => Service.fromFirestore(doc.id, doc.data()))
          .filter(service => service.active === true);
          
        console.log('Successfully fetched services directly from Firestore');
      }
      
      if (!servicesData || !Array.isArray(servicesData)) {
        console.error('Services data is not an array or is null/undefined');
        if (retryCount < 2) {
          console.log(`Retrying service fetch... (${retryCount + 1}/3)`);
          // Wait a moment before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
          return await fetchServices(retryCount + 1);
        } else {
          throw new Error('Maximum retry attempts reached');
        }
      }
      
      console.log(`Successfully fetched ${servicesData.length} services`);
      
      // Log the first few services to verify the data structure
      if (servicesData.length > 0) {
        console.log('Sample service data:');
        servicesData.slice(0, 3).forEach(service => {
          console.log(`- Service ID: ${service.id}, Name: ${service.name}, CategoryID: ${service.categoryId}`);
        });
      } else {
        console.log('No services returned from database');
      }
      
      setServices(servicesData);
      return servicesData;
    } catch (error) {
      console.error('Error fetching services:', error);
      if (retryCount < 2) {
        console.log(`Retrying service fetch after error... (${retryCount + 1}/3)`);
        // Wait a moment before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
        return await fetchServices(retryCount + 1);
      } else {
        setError('Failed to load services from the database. Please try again later.');
        setServices([]);
        throw error;
      }
    }
  };

  // Try to refresh just the services data
  const handleRetryServicesOnly = async () => {
    try {
      setLoading(true);
      setError('');
      await fetchServices();
    } catch (err) {
      console.error('Error in retry:', err);
      setError('Failed to load services. Please reload the page to try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get services for a specific category
  const getCategoryServices = (categoryId) => {
    if (!services || !Array.isArray(services)) return [];
    return services.filter(service => service && service.categoryId === categoryId);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
    setSelectedCategoryFilter('all');
    setShowAllResults(false);
  };

  const handleCategoryFilterChange = (e) => {
    setSelectedCategoryFilter(e.target.value);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col items-center justify-center">
          <div className="animate-spin h-12 w-12 border-b-2 border-pink-600 mb-4"></div>
          <p className="text-gray-600">Loading categories and services...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
          <p className="font-bold">Error</p>
          <p>{error}</p>
          <div className="mt-4 flex space-x-4">
            <button 
              onClick={handleRetryServicesOnly}
              className="px-4 py-2 bg-pink-600 text-white  hover:bg-pink-700 transition-colors duration-300"
            >
              Retry Loading Services
            </button>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-700 transition-colors duration-300"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">No Categories Found</h1>
          <p className="text-gray-600 mb-8">There are currently no decoration categories available.</p>
          <Link to="/" className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Check if services exist
  if (services.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-2">Services Currently Unavailable</h1>
          <p className="text-gray-600 mb-8">Our services are currently being updated. Please check back later.</p>
          <Link to="/" className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
    <PageHero 
        title="Our Decoration Services"
        subtitle="Browse our complete collection of premium decoration services for all your special occasions"
        image="/images/image.jpg"
        />
          
      <div className="container mx-auto px-4 py-12">
      {/* Search Bar with Filters */}
      <div className="mb-12">
        <div className="max-w-xl mx-auto mb-4">
          <div className="flex items-center border-2 rounded-full overflow-hidden border-pink-200 focus-within:border-pink-400 bg-white">
            <FaSearch className="ml-4 text-pink-400" />
            <input
              type="text"
              placeholder="Search for services by name, description, or features..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full py-3 px-4 text-gray-700 focus:outline-none"
            />
            {searchQuery && (
              <button 
                onClick={clearSearch}
                className="px-4 text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>
        
        {/* Category Filter - Show for all views */}
        <div className="max-w-xl mx-auto flex items-center bg-gray-50 p-3">
          <FaFilter className="text-pink-500 mr-3 ml-2" />
          <span className="text-sm text-gray-600 mr-3">Filter by category:</span>
          <select
            value={selectedCategoryFilter}
            onChange={handleCategoryFilterChange}
            className="bg-white border border-gray-200 text-gray-700 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-pink-300 flex-grow"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Search Results */}
      {isSearching && (
        <div className="mb-10">
          <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
            <div className="p-6 bg-gradient-to-r from-pink-50 to-purple-50 flex justify-between items-center">
              <h2 className="text-2xl font-bold" style={{ color: 'var(--primary)' }}>
                Search Results
                <span className="text-lg ml-3 text-gray-500 font-normal">
                  ({searchResults.length} services found)
                </span>
              </h2>
              
              {searchResults.length > 6 && (
                <button 
                  onClick={() => setShowAllResults(!showAllResults)}
                  className="px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition duration-300 text-sm"
                >
                  {showAllResults ? 'Show Less' : 'Show All Results'}
                </button>
              )}
            </div>
            
            {searchResults.length > 0 ? (
              <div className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                  {(showAllResults ? searchResults : searchResults.slice(0, 6)).map(service => (
                    <ServiceCard key={service.id} service={service} />
                  ))}
                </div>
                
                {/* "Show All" button for mobile at the bottom */}
                {searchResults.length > 6 && !showAllResults && (
                  <div className="mt-6 text-center md:hidden">
                    <button 
                      onClick={() => setShowAllResults(true)}
                      className="px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition duration-300"
                    >
                      Show All {searchResults.length} Results
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-10 text-center">
                <p className="text-gray-500 mb-4">No services found matching "<span className="font-medium">{searchQuery}</span>"</p>
                <button 
                  onClick={clearSearch}
                  className="px-4 py-2 bg-pink-100 text-pink-700 rounded-lg hover:bg-pink-200 transition duration-300"
                >
                  Clear Search
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* All Services - Display when not searching */}
      {!isSearching && (
        <div className="space-y-10">
          {categories
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map(category => {
              // Get services for this category
              const categoryServices = getCategoryServices(category.id);
              
              // Skip categories with no services
              if (selectedCategoryFilter !== 'all' && selectedCategoryFilter !== category.id) {
                return null;
              }
              
              if (categoryServices.length === 0) {
                return null;
              }
              
              return (
                <div key={category.id} className="bg-white overflow-hidden">
                  {/* Category Header */}
                  <div className="p-6 bg-gradient-to-r from-pink-50 to-purple-50 flex justify-between items-center">
                    <h1 className="text-3xl font-bold mb-2">{category.name}</h1>
                    <span className="text-gray-500">
                      {categoryServices.length} services
                    </span>
                  </div>

                  {/* Services Grid */}
                  <div className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                      {categoryServices.map(service => (
                        <ServiceCard key={service.id} service={service} />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
    </>
  );
};

const ServiceCard = ({ service }) => {
  if (!service) return null;
  
  const originalPrice = service.price || 0;
  const hasDiscount = service.discount && service.discount > 0;
  const discountedPrice = hasDiscount
    ? Math.round(originalPrice - (originalPrice * service.discount / 100))
    : originalPrice;
  const discountPercentage = hasDiscount ? `-${service.discount}%` : '';

  // Check if service has a YouTube video
  const hasVideo = service.youtubeUrl && service.youtubeUrl.trim() !== '';
  
  return (
    <Link to={`/booking/${service.id}`}>
      <motion.div 
        className="bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow border border-gray-100 cursor-pointer"
        whileHover={{ y: -5 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Image */}
        <div className="h-40 overflow-hidden relative">
          {service.images && service.images.length > 0 ? (
            <img 
              src={service.images[0]} 
              alt={service.name} 
              className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <span className="text-gray-400">No Image</span>
            </div>
          )}
          
          {/* YouTube Video Icon */}
          {hasVideo && (
            <div className="absolute bottom-2 right-2 bg-red-600 text-white p-2 rounded-full shadow-md">
              <FaYoutube size={16} />
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="p-4">
          <h3 className="text-md font-semibold text-gray-800 mb-2 h-12 overflow-hidden">{service.name}</h3>
          {service.discount && service.discount > 0 ? (
            <div className="flex items-center gap-2 mb-2">
              <p className="text-pink-600 font-bold">
                ₹{Math.round(service.price - (service.price * service.discount / 100)).toLocaleString('en-IN')}
              </p>
              <p className="text-sm text-gray-500 line-through">
                ₹{service.price.toLocaleString('en-IN')}
              </p>
              <span className="text-green-600 text-xs font-semibold bg-green-50 px-2 py-1 rounded">
                -{service.discount}%
              </span>
            </div>
          ) : (
            <p className="text-pink-600 font-bold mb-2">
              {service.price ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(service.price) : 'Price on request'}
            </p>
          )}
          
          {/* Features (just show max 2) */}
          {service.features && service.features.length > 0 && (
            <div className="mb-3">
              <ul className="space-y-1">
                {service.features.slice(0, 2).map((feature, index) => {
                  // Handle both old string format and new object format
                  const featureText = typeof feature === 'string' ? feature : feature.text;
                  const featureActive = typeof feature === 'string' ? true : feature.active;
                  
                  return (
                    <li key={index} className="flex items-start text-xs">
                      {featureActive ? (
                        <FaCheck className="text-green-500 mt-1 mr-1 flex-shrink-0 text-xs" />
                      ) : (
                        <FaTimes className="text-red-500 mt-1 mr-1 flex-shrink-0 text-xs" />
                      )}
                      <span className={`truncate ${featureActive ? 'text-gray-600' : 'text-gray-400 line-through'}`}>
                        {featureText}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

export default AllCategories; 